import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Col, Container, Row } from "react-bootstrap";
import {
  FaBox,
  FaCalendar,
  FaChartLine,
  FaComment,
  FaEye,
  FaFilter,
  FaReply,
  FaSearch,
  FaStar,
  FaThumbsUp,
  FaTimes,
} from "react-icons/fa";
import Swal from "sweetalert2";
import "../../../assets/css/seller/reviews/SellerReviews.css";
import Button from "../../../components/general/button/Button";
import Pagination from "../../../components/general/pagination/Pagination";
import SellerLayout from "../../../layouts/SellerLayout";
import MyContext from "../../../context/MyContext";
import { SellerReviewsPageSkeleton } from "../../../components/general/skeleton/SkeletonBlock";
import axios from "axios";

const SellerReviews = () => {
  const navigate = useNavigate();
  const { baseUrl, authToken } = useContext(MyContext);
  const [selectedReview, setSelectedReview] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [ratingFilter, setRatingFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [filteredReviews, setFilteredReviews] = useState([]);

  // ══════════════════════════════════════════════════════════════════════════
  // API FUNCTION — viewProductReviews (GET + Bearer, no kmseller_id)
  // ══════════════════════════════════════════════════════════════════════════
  const viewProductReviews = () => {
    setLoading(true);

    axios
      .get(`${baseUrl}/seller/reviews/viewProductReviews`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        setLoading(false);

        if (response.data.success) {
          setReviews(response.data.reviews);
          setFilteredReviews(response.data.reviews);
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.message,
          });
        }
      })
      .catch((error) => {
        setLoading(false);
        console.log(error);

        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to load product reviews.",
        });
      });
  };

  useEffect(() => {
    if (!authToken) {
      setLoading(false);
      navigate("/seller/login");
      return;
    }
    viewProductReviews();
  }, [authToken]);

  // ── Search + Rating filter ──
  useEffect(() => {
    let result = reviews;

    if (searchText) {
      result = result.filter(
        (r) =>
          r.kmcustomer?.name?.toLowerCase().includes(searchText.toLowerCase()) ||
          String(r.id).includes(searchText)
      );
    }

    if (ratingFilter !== "all") {
      result = result.filter((r) => r.rating === parseInt(ratingFilter));
    }

    setFilteredReviews(result);
    setCurrentPage(1);
  }, [searchText, ratingFilter, reviews]);

  // ── Real stats — backend se aayi `reviews` list se calculate,
  // koi hardcoded value nahi ──
  const averageRating = reviews.length
    ? (reviews.reduce((sum, r) => sum + (r.rating || 0), 0) / reviews.length).toFixed(1)
    : "0.0";

  const positivePercent = reviews.length
    ? Math.round((reviews.filter((r) => r.rating >= 4).length / reviews.length) * 100)
    : 0;

  const stats = [
    {
      icon: <FaStar />,
      color: "orange",
      label: "Average Rating",
      value: averageRating,
    },
    {
      icon: <FaComment />,
      color: "blue",
      label: "Total Reviews",
      value: reviews.length.toString(),
    },
    {
      icon: <FaThumbsUp />,
      color: "green",
      label: "Positive (4★+)",
      value: `${positivePercent}%`,
    },
  ];

  const renderStars = (rating) =>
    [...Array(5)].map((_, i) => (
      <FaStar
        key={i}
        className={i < rating ? "review-star-filled" : "review-star-empty"}
      />
    ));

  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentReviews = filteredReviews.slice(indexOfFirst, indexOfLast);

  if (loading) {
    return (
      <SellerLayout>
        <SellerReviewsPageSkeleton />
      </SellerLayout>
    );
  }

  return (
    <SellerLayout>
      <div className="reviews-container">
        <Container fluid>
          <div className="reviews-header">
            <div>
              <h1>Customer Reviews</h1>
              <p>Manage and respond to customer feedback</p>
            </div>
          </div>

          <Row className="reviews-stats g-3 g-lg-4">
            {stats.map((stat, i) => (
              <Col key={i} xs={12} sm={6} xl={3}>
                <div className="reviews-stat-card">
                  <div className={`reviews-stat-icon ${stat.color}`}>
                    {stat.icon}
                  </div>
                  <div className="reviews-stat-content">
                    <div className="reviews-stat-label">{stat.label}</div>
                    <div className="reviews-stat-value">{stat.value}</div>
                  </div>
                </div>
              </Col>
            ))}
          </Row>

          <div className="reviews-table-section">
            <div className="reviews-table-header">
              <div className="reviews-search">
                <FaSearch />
                <input
                  type="text"
                  placeholder="Search reviews..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              </div>
              <div className="reviews-filters">
                <select
                  value={ratingFilter}
                  onChange={(e) => setRatingFilter(e.target.value)}
                  className="reviews-rating-filter"
                >
                  <option value="all">All Ratings</option>
                  {[5, 4, 3, 2, 1].map((n) => (
                    <option key={n} value={n}>
                      {n} Stars
                    </option>
                  ))}
                </select>
                <Button variant="outline" icon={<FaFilter />}>
                  Filter
                </Button>
              </div>
            </div>

            <div className="reviews-table-wrapper">
              <table className="reviews-table">
                <thead>
                  <tr>
                    <th>CUSTOMER</th>
                    <th>ORDER #</th>
                    <th>RATING</th>
                    <th>REVIEW</th>
                    <th>DATE</th>
                    <th>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {currentReviews.map((review) => (
                    <tr key={review.id}>
                      <td>
                        <div>
                          <strong>{review.kmcustomer?.name}</strong>
                        </div>
                      </td>
                      <td>#{review.id}</td>
                      <td>
                        <div className="reviews-rating">
                          {renderStars(review.rating)}
                        </div>
                      </td>
                      <td className="reviews-comment-preview">
                        {review.review}
                      </td>
                      <td>{review.created_at}</td>
                      <td>
                        {/* Sirf ek hi action — dropdown ki jagah direct icon */}
                        <button
                          className="ksa-icon-action-btn"
                          title="View Details"
                          onClick={() => setSelectedReview(review)}
                        >
                          <FaEye />
                        </button>
                      </td>
                    </tr>
                  ))}

                  {currentReviews.length === 0 && (
                    <tr>
                      <td colSpan={6} style={{ textAlign: "center", padding: "20px" }}>
                        No reviews found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <Pagination
              totalItems={filteredReviews.length}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={(n) => {
                setItemsPerPage(n);
                setCurrentPage(1);
              }}
            />
          </div>

          {/* ══════════ VIEW DETAILS MODAL ══════════ */}
          {selectedReview && (
            <div
              className="ksa-modal-overlay"
              onClick={() => setSelectedReview(null)}
            >
              <div
                className="ksa-modal ksa-modal-lg ksa-category-modal"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="ksa-category-modal-header">
                  <h3 className="ksa-category-modal-title">Review Details</h3>
                  <button
                    className="ksa-modal-close"
                    onClick={() => setSelectedReview(null)}
                  >
                    <FaTimes size={18} />
                  </button>
                </div>

                <div className="ksa-category-modal-body">
                  <div className="reviews-detail-card">
                    <div className="reviews-detail-customer">
                      <div className="reviews-customer-info">
                        <h5>{selectedReview.kmcustomer?.name}</h5>
                      </div>
                    </div>
                    <div className="reviews-detail-meta">
                      <div className="reviews-meta-item">
                        <FaBox />
                        <span>Order #{selectedReview.id}</span>
                      </div>
                      <div className="reviews-meta-item">
                        <FaCalendar />
                        <span>{selectedReview.created_at}</span>
                      </div>
                    </div>
                  </div>

                  <div className="reviews-detail-card" style={{ marginTop: "1rem" }}>
                    <div className="reviews-detail-rating">
                      <div className="reviews-rating-stars">
                        {renderStars(selectedReview.rating)}
                      </div>
                      <span className="reviews-rating-value">
                        {selectedReview.rating}.0 out of 5
                      </span>
                    </div>
                    <div className="reviews-detail-comment">
                      <h6>Review Comment</h6>
                      <p>{selectedReview.review}</p>
                    </div>
                    {selectedReview.reply && (
                      <div className="reviews-seller-reply">
                        <h6>
                          <FaReply /> Your Reply
                        </h6>
                        <p>{selectedReview.reply}</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="ksa-category-modal-footer">
                  <button
                    className="outline-gradient"
                    onClick={() => setSelectedReview(null)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
        </Container>
      </div>
    </SellerLayout>
  );
};

export default SellerReviews;
